import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {SelectInput} from 'admin-on-rest';
import {sortArrayByKey} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import LocationsTableFilter from './LocationsTableFilter';
import {VirtualizedSelectInput} from './VirtualizedSelectInput';
import {filterStyles} from '../shared/styles-filter';

/**
 * A date-range reliant form containing secondary filters and a Location array builder *
 */
class SecondaryFilterWithLocations extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filterListDBAs: [],
            filterListDistricts: [],
            filterListLocations: [],
            filterListStateCities: [],
            filterListStateDBAs: [],
            filterListStates: [],
            filterListStatesWithCities: [],
            filterListStatesWithDBAs: [],
            filterListTypes: [],
            filterOptionList: [],
            selectedFilterType: 'all',
            selectedFirstChoice: null,
            selectedSecondChoice: null
        };
        this.fetchFilterData = this.fetchFilterData.bind(this);
        this.handleSelectFilterType = this.handleSelectFilterType.bind(this);
        this.handleSelectFirstFilterOption = this.handleSelectFirstFilterOption.bind(this);
        this.handleSelectSecondFilterOption = this.handleSelectSecondFilterOption.bind(this);
    }

    componentDidMount() {
        this.fetchFilterData();
    }

    componentWillReceiveProps(nextProps) {
        let clearFilters = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset ||
            nextProps.resource !== this.props.resource
        ) {
            clearFilters = true;
        }
        if (clearFilters) {
            // Reset all data due to change in Primary filter
            this.setState({
                filterListStateCities: [],
                filterListStateDBAs: [],
                filterListDBAs: [],
                filterListDistricts: [],
                filterListLocations: [],
                filterListStates: [],
                filterListStatesWithCities: [],
                filterListStatesWithDBAs: [],
                filterListTypes: [],
                filterOptionList: [],
                selectedFilterType: 'all',
                selectedFirstChoice: null,
                selectedSecondChoice: null
            }, () => {
                // Lift state of changed Secondary Filter data
                // (This will clear Secondary filter, as opposed to preserving user's selection)
                this.props.onFilterTypeSelect('all');
                this.props.onFilterChoiceSelect(null, 'none');
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset ||
            prevProps.resource !== this.props.resource
        ) {
            this.fetchFilterData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get list of Locations for Location filter
     */
    fetchFilterData() {
        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };
        const resource = this.props.resource.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        // Retrieve available filter data
        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload) {
                this.processFilterData(result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store available arrays to populate filter
     *
     * @param data
     */
    processFilterData(data) {
        let filterListDBAs = [];
        let filterListDistricts = [];
        let filterListLocations = [];
        let filterListStates = [];
        let filterListStatesWithCities = [];
        let filterListStatesWithDBAs = [];
        let filterListTypes = [];

        if (data.aggregations) {
            // Process available DBAs and add to options list
            if (data.aggregations.DBAs) {
                let dbas = data.aggregations.DBAs.buckets;
                if (dbas && dbas.length) {
                    for (let d = 0; d < dbas.length; d++) {
                        let dbaItem = dbas[d];
                        let dba = dbaItem.column_name.hits.hits[0]._source.rxtxMsg.payload.location.dba;
                        if (dba) {
                            filterListDBAs.push({id: dba.id, name: dba.name});
                        }
                    }
                    filterListDBAs = sortArrayByKey(filterListDBAs, 'name', 'ASC');
                }
            }

            // Process available Districts and add to options list
            if (data.aggregations.Districts) {
                let districts = data.aggregations.Districts.buckets;
                if (districts && districts.length) {
                    for (let di = 0; di < districts.length; di++) {
                        let districtItem = districts[di];
                        let district = districtItem.column_name.hits.hits[0]._source.rxtxMsg.payload.location.district;
                        if (district) {
                            filterListDistricts.push({id: district.id, name: district.name});
                        }
                    }
                    filterListDistricts = sortArrayByKey(filterListDistricts, 'name', 'ASC');
                }
            }

            // Process available Locations and add to option list
            if (data.aggregations.Locations) {
                let locations = data.aggregations.Locations.buckets;
                if (locations && locations.length) {
                    for (let l = 0; l < locations.length; l++) {
                        let locationItem = locations[l];
                        let location = locationItem.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                        if (location) {
                            filterListLocations.push({id: location.id, name: location.id + ' - ' + location.name});
                        }
                    }
                    filterListLocations = sortArrayByKey(filterListLocations, 'name', 'ASC');
                }
            }

            // Process available States with additional arrays of Cities or DBAs
            if (data.aggregations.States) {
                let states = data.aggregations.States.buckets;
                if (states && states.length) {
                    for (let s = 0; s < states.length; s++) {
                        let stateItem = states[s];
                        let state = stateItem.column_name.hits.hits[0]._source.rxtxMsg.payload.location.state;
                        // Do not include edge-case states that aren't uppercase (e.g., Ok vs OK)
                        if (state && (state.toUpperCase() === state)) {
                            // Each state may store a list of its cities
                            let cities = stateItem.cities_by_state.buckets;
                            let cityList = [];
                            if (cities && cities.length) {
                                for (let c = 0; c < cities.length; c++) {
                                    let city = cities[c].column_name.hits.hits[0]._source.rxtxMsg.payload.location.city;
                                    if (city) {
                                        cityList.push({key: city.trim() + 'c', id: city.trim(), name: city.trim()});
                                    }
                                }
                                filterListStatesWithCities.push({id: state, name: state, cities: cityList});
                            }
                            cityList = sortArrayByKey(cityList, 'name', 'ASC');
                            filterListStates.push({id: state, name: state, cities: cityList});

                            // Each state may store a list of its DBAs
                            let stateDbas = stateItem.dbas_by_state.buckets;
                            let stateDbaList = [];
                            if (stateDbas && stateDbas.length) {
                                for (let sd = 0; sd < stateDbas.length; sd++) {
                                    let stateDba = stateDbas[sd].column_name.hits.hits[0]._source.rxtxMsg.payload.location.dba;
                                    if (stateDba) {
                                        stateDbaList.push({
                                            key: stateDba.id.toString() + sd.toString(),
                                            id: stateDba.id,
                                            name: stateDba.name
                                        });
                                    }
                                }
                                stateDbaList = sortArrayByKey(stateDbaList, 'name', 'ASC');
                                filterListStatesWithDBAs.push({id: state, name: state, dbas: stateDbaList});
                            }
                        }
                    }
                    filterListStates = sortArrayByKey(filterListStates, 'name', 'ASC');
                    filterListStatesWithCities = sortArrayByKey(filterListStatesWithCities, 'name', 'ASC');
                    filterListStatesWithDBAs = sortArrayByKey(filterListStatesWithDBAs, 'name', 'ASC');
                }
            }
        }

        // Populate Filter Types
        filterListTypes.push({id: 'all', name: 'All'});
        if (filterListDBAs.length) {
            filterListTypes.push({id: 'dba', name: 'by DBA'});
        }
        if (filterListDistricts.length) {
            filterListTypes.push({id: 'district', name: 'by District'});
        }
        if (filterListLocations.length) {
            filterListTypes.push({id: 'location', name: 'by Location ID'});
        }
        if (filterListStates.length) {
            filterListTypes.push({id: 'state', name: 'by State'});
        }
        if (filterListStatesWithCities.length) {
            filterListTypes.push({id: 'statecity', name: 'by State and City'});
        }
        if (filterListStatesWithDBAs.length) {
            filterListTypes.push({id: 'statedba', name: 'by State and DBA'});
        }
        filterListTypes.push({id: 'locations', name: 'by Specific Locations'});

        this.setState({
            filterListDBAs: filterListDBAs,
            filterListDistricts: filterListDistricts,
            filterListLocations: filterListLocations,
            filterListStateCities: [],
            filterListStateDBAs: [],
            filterListStates: filterListStates,
            filterListStatesWithCities: filterListStatesWithCities,
            filterListStatesWithDBAs: filterListStatesWithDBAs,
            filterListTypes: filterListTypes
        });
    }

    /**
     * Handle selection event for Filter Type
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectFilterType(event, index, value) {
        if (index) {
            let filterObj = {};
            let filterTypes = this.state.filterListTypes;
            let filterType = filterTypes.find(f => f.id === index);
            if (filterType) {
                filterObj = filterType;
            }

            this.setState({
                filterListStateCities: [],
                filterListStateDBAs: [],
                selectedFilterType: index,
                selectedFirstChoice: null,
                selectedSecondChoice: null
            }, () => {
                this.props.onFilterTypeSelect(filterObj);
                // If Secondary filter is set to All, clear any second selection
                if (filterObj === 'all') {
                    this.props.onFilterChoiceSelect(null, 'none');
                }
            });
        }
    }

    /**
     * Handle selection event for First Filter Choice
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectFirstFilterOption(event, index, value) {
        if (index) {
            // If options require two filters:
            let states;
            let selectedState;
            if (this.state.selectedFilterType === 'statecity') {
                // Populate the list of Cities
                states = this.state.filterListStatesWithCities;
                let stateCities = [];
                selectedState = states.find(s => s.id === index);
                if (selectedState) {
                    stateCities = selectedState.cities;
                }
                this.setState({
                    filterListStateCities: stateCities,
                    selectedFirstChoice: index
                }, () => {
                    // Lift partial state until second filter is chosen
                    this.props.onFilterChoiceSelect(index, null);
                });
            } else if (this.state.selectedFilterType === 'statedba') {
                states = this.state.filterListStatesWithDBAs;
                let stateDBAs = [];
                selectedState = states.find(d => d.id === index);
                if (selectedState) {
                    stateDBAs = selectedState.dbas;
                }
                this.setState({
                    filterListStateDBAs: stateDBAs,
                    selectedFirstChoice: index
                }, () => {
                    // Lift partial state until second filter is chosen
                    this.props.onFilterChoiceSelect(index, null);
                });
            } else {
                // Otherwise, lift state
                this.setState({
                    selectedFirstChoice: index
                }, () => {
                    this.props.onFilterChoiceSelect(index, 'none');
                });
            }
        }
    }

    /**
     * Handle selection event for Second Filter Choice
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectSecondFilterOption(event, index, value) {
        if (index) {
            this.setState({
                selectedSecondChoice: index
            }, () => {
                // Also pass the current first filter choice
                this.props.onFilterChoiceSelect(this.state.selectedFirstChoice, index);
            });
        }
    }

    render() {
        const {
            filterListDBAs,
            filterListDistricts,
            filterListLocations,
            filterListStateCities,
            filterListStateDBAs,
            filterListStates,
            filterListStatesWithCities,
            filterListStatesWithDBAs,
            filterListTypes,
            selectedFilterType,
            selectedFirstChoice,
            selectedSecondChoice
        } = this.state;

        // Create labels and set option lists based on Filter Type
        let firstFilterLabel = '';
        let secondFilterLabel = '';
        let firstFilterOptions = [];
        let secondFilterOptions = [];

        if (selectedFilterType === 'dba') {
            firstFilterLabel = 'Select a DBA';
            firstFilterOptions = filterListDBAs;
        } else if (selectedFilterType === 'district') {
            firstFilterLabel = 'Select a District';
            firstFilterOptions = filterListDistricts;
        } else if (selectedFilterType === 'location') {
            firstFilterLabel = 'Select a Location';
            firstFilterOptions = filterListLocations;
        } else if (selectedFilterType === 'state') {
            firstFilterLabel = 'Select a State';
            firstFilterOptions = filterListStates;
        } else if (selectedFilterType === 'statecity') {
            firstFilterLabel = 'Select a State';
            secondFilterLabel = 'Select a City';
            firstFilterOptions = filterListStatesWithCities;
            secondFilterOptions = filterListStateCities;
        } else if (selectedFilterType === 'statedba') {
            firstFilterLabel = 'Select a State';
            secondFilterLabel = 'Select a DBA';
            firstFilterOptions = filterListStatesWithDBAs;
            secondFilterOptions = filterListStateDBAs;
        }

        // Create styles for first select filter (for States)
        const firstStyle = (selectedFilterType === 'state' || selectedFilterType === 'statecity')
            ? filterStyles.selectInputSmall
            : filterStyles.selectInput;

        return (
            <div className="list-filter">
                <span>Select a secondary option to filter data by.</span>
                <form>
                    {filterListTypes && filterListTypes.length > 0 &&
                        <Field name="filter_types"
                               component={SelectInput}
                               label="Filter Summaries:"
                               choices={filterListTypes}
                               onChange={this.handleSelectFilterType}
                               elStyle={filterStyles.selectInput}
                               options={{
                                   value: selectedFilterType
                               }}
                        />
                    }
                    {selectedFilterType && selectedFilterType !== 'all' &&
                        <span>
                            {firstFilterOptions.length > 0 &&
                                <Field name="filter_first_choice"
                                       component={VirtualizedSelectInput}
                                       choices={firstFilterOptions}
                                       label={firstFilterLabel}
                                       optionText="name"
                                       optionValue="id"
                                       onChange={this.handleSelectFirstFilterOption}
                                       elStyle={firstStyle}
                                       allowEmpty
                                       options={{
                                           className: 'has-label inline',
                                           clearable: true,
                                           value: selectedFirstChoice,
                                           placeholder: firstFilterLabel
                                       }}
                                />
                            }
                            {(selectedFilterType === 'statecity' || selectedFilterType === 'statedba') &&
                                <Field name="filter_second_choice"
                                       component={VirtualizedSelectInput}
                                       choices={secondFilterOptions}
                                       label={secondFilterLabel}
                                       optionText="name"
                                       optionValue="id"
                                       onChange={this.handleSelectSecondFilterOption}
                                       elStyle={filterStyles.selectInput}
                                       allowEmpty
                                       isLoading={false}
                                       options={{
                                           className: 'has-label inline',
                                           clearable: true,
                                           placeholder: secondFilterLabel,
                                           value: selectedSecondChoice
                                       }}
                                />
                            }
                            {selectedFilterType === 'locations' &&
                                <LocationsTableFilter
                                    onLocations={this.handleSelectSecondFilterOption}
                                />
                            }
                    </span>
                    }
                    <hr style={filterStyles.break}/>
                </form>
            </div>
        );
    }
}

SecondaryFilterWithLocations.propTypes = {
    addField: PropTypes.bool,
    basePath: PropTypes.string,
    endDate: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onFilterTypeSelect: PropTypes.func,
    onFilterChoiceSelect: PropTypes.func,
    resource: PropTypes.object.isRequired,
    startDate: PropTypes.string,
};

SecondaryFilterWithLocations.defaultProps = {
    addField: true,
    endDate: null,
    offset: null,
    resource: null,
    startDate: null,
};

const enhance = compose(
    connect(),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
    })
);

export default enhance(SecondaryFilterWithLocations);



// WEBPACK FOOTER //
// ./src/Fuse/components/SecondaryFilterWithLocations.js